import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'
import {
  metricsToBackendConfig,
  type CampaignShortItem,
  type Disposition,
  type MetricConfig,
  type Agent,
  type CampaignFull,
  type DispositionCode,
} from '@intone/shared/types';

// Fetch list of campaigns (short list for selector)
export function useCampaignsList() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['campaigns-list'],
    queryFn: async () => {
      return apiClient<CampaignShortItem[]>('/campaigns')
    },
  })
}

// Fetch all campaigns with full data
export function useCampaignsFull() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['campaigns-full'],
    queryFn: async () => {
      return apiClient<CampaignFull[]>('/campaigns/full')
    },
  })
}

// Fetch single campaign by ID
export function useCampaign(id: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['campaigns', id],
    queryFn: async () => {
      return apiClient<CampaignFull>(`/campaigns/${id}`)
    },
    enabled: !!id && id > 0,
  })
}

// Campaign save input type
export interface SaveCampaignInput {
  id: number
  name: string
  direction: 'inbound' | 'outbound'
  language: string
  country: string | null
  telephony: string | null
  agents: Agent[]
  dispositions: Disposition[]
  metrics: MetricConfig[]
}

// Save campaign (create or update)
export function useSaveCampaign() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaign: SaveCampaignInput) => {
      // Convert metrics to backend format
      const backendMetrics = metricsToBackendConfig(campaign.metrics)

      const response = await apiClient<{ campaignId: number; message: string }>(
        '/campaigns',
        {
          method: 'POST',
          body: JSON.stringify({
            ...campaign,
            metrics: backendMetrics,
          }),
        }
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-list'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns-full'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

// Delete campaign
export function useDeleteCampaign() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaignId: number) => {
      return apiClient<{ message: string }>(`/campaigns/${campaignId}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-list'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns-full'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useCampaignAvailableDispositions(campaignId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['campaign-available-dispositions', campaignId],
    queryFn: async () => {
      return apiClient<DispositionCode[]>(`/campaigns/${campaignId}/available-dispositions`);
    },
    enabled: !!campaignId
  });
}

export function useDuplicateCampaign() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaignId: number) => {
      return apiClient<{ campaignId: number; message: string }>(
        `/campaigns/${campaignId}/duplicate`,
        { method: 'POST' }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-list'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns-full'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

// Legacy exports for backward compatibility
export const useCampaigns = useCampaignsList
export const useCreateCampaign = useSaveCampaign
export const useUpdateCampaign = useSaveCampaign